import styled from 'styled-components';
import { MiniUserProfile as MiniUserProfileBase } from 'components/Toolkit/MiniUserProfile/MiniUserProfile';
import { PrivateSellerRatings as PrivateSellerRatingsBase } from 'components/PageWrappers/AdDetails/components/PrivateSellerRatings/PrivateSellerRatings';
import { media } from 'helpers/breakpoints';
import { DividerBulletFilledIc, StarIc } from '@dsch/dd-icons';
import { TabSelector } from 'components/Toolkit/TabSelector/TabSelector';

const SellerInfoPanelWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: ${({ theme }) => theme.spacing.M24};
  // We add a scroll margin here as this is the scroll target but there is no top
  // padding or margin on this container so the content would be flush with the
  // edge of the screen, we would like to give some space for readability and
  // aesthetics. We chose M24 as that is the spacing to the content divider above
  // this panel.
  scroll-margin: ${({ theme }) => theme.spacing.M24};

  ${media.medium} {
    grid-template-columns: 1fr 1fr;
  }

  // We want to programmatically focus the element but not highlight it (Safari/iOS)
  &:focus-visible {
    outline: none;
  }
`;

const ProfileSummary = styled.div`
  // Allow div to collapse for text truncation
  min-width: 0;
  ${media.medium} {
    grid-column: 1 / 2;
    margin-bottom: ${({ theme }) => theme.spacing.S8};
  }
`;

const MiniUserProfile = styled(MiniUserProfileBase)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const ActionsAdStats = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  ${media.medium} {
    grid-column: 2 / 3;
  }
`;

const ButtonList = styled.ul`
  display: none;

  ${media.medium} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.S8};
    margin-bottom: ${({ theme }) => theme.spacing.M16};
  }
`;

const ButtonListItem = styled.li<{ hideOnMobile?: boolean }>`
  display: ${({ hideOnMobile }) => (hideOnMobile ? 'none' : 'block')};

  ${media.medium} {
    display: block;
  }
`;

const Divider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.GREY_LIGHT};
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  ${media.medium} {
    margin-bottom: ${({ theme }) => theme.spacing.S8};
    grid-column: 1 / 3;
  }
`;

const ReviewCount = styled.span`
  ${({ theme }) => theme.fontSize.M16}
  color: ${({ theme }) => theme.colors.GREY_DARK};
`;

const MessageStats = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.S8};
`;

const AdStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.S8};
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.S8};
  ${({ theme }) => theme.fontSize.M14}
  color: ${({ theme }) => theme.colors.GREY_DARK};
`;

const Title = styled.p`
  ${({ theme }) => theme.fontSize.M14}
  color: ${({ theme }) => theme.colors.GREY_DARK};
`;

const RatingTitle = styled.h2`
  ${({ theme }) => theme.fontSize.L18}
  margin-bottom: ${({ theme }) => theme.spacing.S4};
`;

const ReviewTitle = styled(RatingTitle)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const RatingSubTitle = styled.p`
  ${({ theme }) => theme.fontSize.M16}
  color: ${({ theme }) => theme.colors.GREY_DARK};
  margin-top: ${({ theme }) => theme.spacing.S4};
`;

const PrivateSellerRatings = styled(PrivateSellerRatingsBase)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  ${media.medium} {
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

const RatingsFeedback = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: ${({ theme }) => theme.spacing.M16};
  // We add a scroll margin here as this is the scroll target but there is no top
  // padding or margin on this container so the content would be flush with the
  // edge of the screen, we would like to give some space for readability and
  // aesthetics. We chose M16 as that is the spacing to the content divider above
  // this panel. On desktop we have spacing by default, so the scroll position
  // is acceptable
  scroll-margin: ${({ theme }) => theme.spacing.M16};

  ${media.medium} {
    grid-column: 1 / 3;
    grid-template-columns: 1fr 2fr;
    column-gap: ${({ theme }) => theme.spacing.M24};
    padding: ${({ theme }) => `${theme.spacing.M24} 0`};
    scroll-margin: 0;
  }

  // We want to programmatically focus the element but not highlight it (Safari/iOS)
  &:focus-visible {
    outline: none;
  }
`;

const Ratings = styled.div<{ fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  ${media.medium} {
    margin-bottom: ${({ theme }) => theme.spacing.S8};
    grid-column: ${({ fullWidth }) => (fullWidth ? '1 / 3' : '1 / 2')};
  }
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.M24};
`;

const RatingText = styled.p`
  ${({ theme }) => theme.fontSize.M16}
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
`;

const RatingsSummary = styled.div`
  ${({ theme }) => theme.fontSize.M14}
  color: ${({ theme }) => theme.colors.GREY_DARK};
  display: flex;
  flex-flow: column nowrap;
  gap: ${({ theme }) => theme.spacing.S8};
`;

const StarIcon = styled(StarIc)`
  margin-right: ${({ theme }) => theme.spacing.S4};
`;

const Bullet = styled(DividerBulletFilledIc)`
  margin: ${({ theme }) => `0 ${theme.spacing.S8}`};
`;

const Feedback = styled.div`
  display: flex;
  flex-flow: column;

  ${media.medium} {
    grid-column: 2 / 3;
  }
`;

const FeedbackSelector = styled(TabSelector)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  border-bottom: 1px solid ${({ theme }) => theme.colors.GREY_LIGHT};
`;

const Tags = styled.ul`
  display: flex;
  gap: ${({ theme }) => theme.spacing.S8};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  ${media.medium} {
    max-height: 118px; // Tags are not allowed to expand beyond 3 rows a tag is 34px high with 8px gap = 34 x 3 + 8 x 2 = 118
    margin-bottom: 0;
    overflow: hidden;
  }
`;

const DisplayTagSpacer = styled.span`
  margin: ${({ theme }) => `0 ${theme.spacing.S4}`};
`;

const TabItem = styled.span`
  ${({ theme }) => theme.fontSize.L18}
  color: ${({ theme }) => theme.colors.GREY_DARKER};
`;

const ReviewSubTitle = styled.p`
  ${({ theme }) => theme.fontSize.M16}
  color: ${({ theme }) => theme.colors.GREY_DARK};
  margin-bottom: ${({ theme }) => theme.spacing.M16};
  flex: 1 1 auto;
  align-self: center;
  align-content: center;
`;

const SafeHelpSection = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.S4};
  flex-wrap: wrap;
  ${media.medium} {
    grid-column: 1 / 3;
  }
`;

export {
  SellerInfoPanelWrapper,
  ProfileSummary,
  ButtonList,
  ButtonListItem,
  ActionsAdStats,
  Divider,
  MessageStats,
  AdStats,
  StatsRow,
  Title,
  MiniUserProfile,
  PrivateSellerRatings,
  RatingsFeedback,
  Ratings,
  RatingsSummary,
  RatingRow,
  RatingText,
  StarIcon,
  Bullet,
  ReviewCount,
  RatingTitle,
  ReviewTitle,
  RatingSubTitle,
  Feedback,
  FeedbackSelector,
  Tags,
  DisplayTagSpacer,
  TabItem,
  ReviewSubTitle,
  SafeHelpSection,
};
